'use client';

import { c as _c } from "react/compiler-runtime";
import { LocalizedHtml, useI18n } from '@udemy-v2/i18n';
import { AlertBanner } from '@udemy-v2/react-notice-components';
import { Loader } from '@udemy-v2/react-reveal-components';
import { useEnrolledInCourse } from '@/features/course-landing-page/hooks/use-enrolled-in-course';
import { UnavailableCourseCategoryCarousel, UnavailableCourseTopicCarousel } from '@/features/course-landing-page/components/limited-access-recommendations';
import { getCourseLearnUrl } from '@/lib/utils';
import styles from './unavailable-course.module.scss';
import { OnePaneLayoutContainer } from '@udemy-v2/react-layout-components';
import { useIsLoggedIn } from "@/lib/hooks/use-is-logged-in";
interface UnavailableCourseProps {
  /** Course id from the ACCESS_RESTRICTED extension; required to look up enrollment. */
  courseId?: string;
  /** Source URL (e.g. https://udemy.com/course/foo/) used to derive the /learn/ link. */
  courseLandingUrl: string;
  /** topic, category, subcategory IDs threaded through from the extension. Used in phase 2. */
  topicId?: string;
  categoryId?: string;
  subcategoryId?: string;
}
interface UnavailableCourseBannerProps {
  /** True when the viewer is enrolled — surfaces the /learn/ deep link. */
  isEnrolled: boolean;
  courseLandingUrl: string;
}

/**
 * Pure presentational banner. Exported so Storybook can render both
 * enrollment branches without standing up a GraphQL mock.
 */
export function UnavailableCourseBanner(t0) {
  const $ = _c(19);
  const {
    isEnrolled,
    courseLandingUrl
  } = t0;
  const {
    gettext,
    interpolate
  } = useI18n();
  const isLoggedIn = useIsLoggedIn();
  let t1;
  if ($[0] !== courseLandingUrl) {
    t1 = getCourseLearnUrl(courseLandingUrl);
    $[0] = courseLandingUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const learnUrl = t1;
  let t2;
  if ($[2] !== gettext || $[3] !== interpolate) {
    t2 = interpolate(gettext("Since you previously enrolled in this course, you can still access it <a class=\"course-learn-link\">here</a>."), {}, true);
    $[2] = gettext;
    $[3] = interpolate;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== learnUrl) {
    t3 = {
      "course-learn-link": <a className="ud-link-underline" href={learnUrl} />
    };
    $[5] = learnUrl;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <LocalizedHtml html={t2} interpolate={t3} />;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const enrolledMessage = t4;
  let t5;
  if ($[10] !== gettext) {
    t5 = gettext("Our apologies for any inconvenience.");
    $[10] = gettext;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const unenrolledMessage = t5;
  let t6;
  if ($[12] !== gettext) {
    t6 = gettext("If you previously enrolled in this course, please log in to access.");
    $[12] = gettext;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const loggedOutMessage = t6;
  const body = isLoggedIn ? isEnrolled ? enrolledMessage : unenrolledMessage : loggedOutMessage;
  let t7;
  if ($[14] !== gettext) {
    t7 = gettext("Sorry, this course is no longer accepting enrollments.");
    $[14] = gettext;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== body || $[17] !== t7) {
    t8 = <AlertBanner udStyle="warning" showCta={false} iconDismissButton={false} title={t7} body={body} className={styles["alert-banner"]} />;
    $[16] = body;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}

/**
 * Renders the enrollment-aware banner inside `UnavailableCourse`. Owns the
 * enrollment query so that the parent can render the recommendation carousels
 * unconditionally — without blocking on enrollment load.
 */
function UnavailableCourseBannerWithEnrollment(t0) {
  const $ = _c(4);
  const {
    courseId,
    courseLandingUrl
  } = t0;
  const enrollment = useEnrolledInCourse(courseId ?? "");
  const isEnrolled = courseId ? enrollment : false;
  if (courseId && isEnrolled === undefined) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className={styles["loading-container"]}><Loader size="xxlarge" /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = isEnrolled === true;
  let t2;
  if ($[1] !== courseLandingUrl || $[2] !== t1) {
    t2 = <UnavailableCourseBanner isEnrolled={t1} courseLandingUrl={courseLandingUrl} />;
    $[1] = courseLandingUrl;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

/**
 * Rendered by `LimitedAccessCLP` for the `UNAVAILABLE` restriction. Shows a
 * warning banner explaining the course no longer accepts new enrollments;
 * the subtitle switches based on whether the current user is already
 * enrolled (in which case we surface a deep link to `/learn/`).
 *
 */
export function UnavailableCourse(props) {
  const $ = _c(12);
  const {
    courseId,
    courseLandingUrl,
    topicId,
    categoryId,
    subcategoryId
  } = props;
  let t0;
  if ($[0] !== courseId || $[1] !== courseLandingUrl) {
    t0 = <UnavailableCourseBannerWithEnrollment courseId={courseId} courseLandingUrl={courseLandingUrl} />;
    $[0] = courseId;
    $[1] = courseLandingUrl;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== topicId) {
    t1 = <UnavailableCourseTopicCarousel topicId={topicId} />;
    $[3] = topicId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== categoryId || $[6] !== subcategoryId) {
    t2 = <UnavailableCourseCategoryCarousel categoryId={categoryId} subcategoryId={subcategoryId} />;
    $[5] = categoryId;
    $[6] = subcategoryId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = <OnePaneLayoutContainer><div className={styles["recommendations-container"]}>{t0}{t1}{t2}</div></OnePaneLayoutContainer>;
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}